// src/pages/Home.jsx
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Element, scroller } from 'react-scroll';
import '../styles/Home.css';
import Contact from './ContactUs'; // adjust path if needed
import FinalVideo from '../Final Video.mp4'; // update path if necessary
import logo from '../logo.png'; // update path if necessary

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const NAV_OFFSET = 72;

  const now = new Date();
  const yearsInOperation = Math.max(0, now.getFullYear() - 1996);

  useEffect(() => {
    const s = location.state || {};

    // If we were asked to scroll to contact, do that
    if (s.scrollToContact) scrollTo('contact-section');

    if (s.scrollToAbout) scrollTo('about-us-section');
    if (s.scrollToProductsServices) scrollTo('products-services-section');

    // Note: we do not clear location.state here, so Contact component can read any prefill
    // If you prefer to remove state after reading, you can call navigate(location.pathname, { replace: true, state: {} })
    // but leave as-is to let the Contact component see location.state.contact.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const scrollTo = (name) =>
    scroller.scrollTo(name, { duration: 700, smooth: 'easeInOutQuart', offset: -NAV_OFFSET });

  const goToHistory = () => navigate('/history', { state: { scrollToTop: true } });
  const goToProducts = () => navigate('/products-services', { state: { scrollToTop: true } });

  return (
    <main className="hi-root">
      {/* HERO */}
      <section className="hi-hero" role="banner" aria-label="Company hero">
        <div className="hi-media" aria-hidden>
          <video
            className="hi-video"
            autoPlay
            muted
            loop
            playsInline
            poster="/assets/hero-poster.jpg"
          >
            <source src={FinalVideo} type="video/mp4" />
          </video>
          <div className="hi-media-overlay" />
        </div>

        <div className="hi-hero-content container">
          <img src={logo} alt="Afework International Group logo" className="hi-logo" />
          <h1 className="hi-title">Quality medical & industrial imports — trusted since 1996</h1>
          <p className="hi-sub">
            Rigorous quality checks, dependable logistics, and local support — supplying hospitals, institutions and businesses with trusted equipment and supplies.
          </p>

          <div className="hi-cta-row">
            <button className="btn primary" onClick={goToProducts} aria-label="View product catalogue">
              View catalogue
            </button>
            <button className="btn ghost" onClick={() => scrollTo('about-us-section')} aria-label="Learn more">
              Learn more
            </button>
            <button className="hero-scroll" onClick={() => scrollTo('about-us-section')} aria-hidden>
              <span className="chev" />
            </button>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <Element name="about-us-section" />
      <section className="hi-section hi-about container">
        <div className="hi-grid hi-about-grid">
          <div>
            <h2>About Afework International Group</h2>
            <p className="lead">
              For <strong>{yearsInOperation} years</strong> we've imported and delivered medical equipment, diagnostics and industrial supplies — partnering with vetted manufacturers and trusted logistics providers to bring compliant, reliable products to market.
            </p>
            <p className="muted">
              We focus on compliance, documentation, and after-sales support so facilities can operate with confidence.
            </p>

            <div className="kpis" style={{ alignItems: 'center' }}>
              <div className="kpi">
                <div className="kpi-num">1996</div>
                <div className="kpi-label">Founded</div>
              </div>

              <div className="kpi">
                <div className="kpi-num">{yearsInOperation}+</div>
                <div className="kpi-label">Years</div>
              </div>

              <div className="kpi" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div>
                    <div className="kpi-num">100+</div>
                    <div className="kpi-label">Products</div>
                  </div>

                  {/* Our history button rendered next to the Products tile */}
                </div>
              </div>

              <button
                className="btn outline"
                onClick={goToHistory}
                aria-label="Company history"
                style={{ padding: '10px 12px', fontSize: '13px', borderRadius: 10, marginLeft: 12 }}
              >
                Our history
              </button>
            </div>
          </div>

          <aside className="hi-card small-cta">
            <h3>Get equipment fast</h3>
            <p className="muted">Request a quote, schedule a call, or browse our catalogue for product details.</p>
            <div className="card-actions">
              <button className="btn primary" onClick={goToProducts}>Request quote</button>
              <button className="btn outline" onClick={() => scrollTo('contact-section')}>Contact sales</button>
            </div>
          </aside>
        </div>
      </section>

      {/* WHY */}
      <section className="hi-section container hi-why">
        <h3>Why choose us</h3>
        <div className="services-grid">
          <article className="service">
            <div className="service-icon">🔒</div>
            <h4>Compliance & QA</h4>
            <p className="muted">Thorough documentation and product testing before shipping.</p>
          </article>

          <article className="service">
            <div className="service-icon">🚚</div>
            <h4>Reliable logistics</h4>
            <p className="muted">Trusted freight & warehousing partners to ensure on-time delivery.</p>
          </article>

          <article className="service">
            <div className="service-icon">🤝</div>
            <h4>Long-term partnerships</h4>
            <p className="muted">We build relationships with suppliers and clients to deliver consistent value.</p>
          </article>

          <article className="service">
            <div className="service-icon">⚙️</div>
            <h4>Installation & support</h4>
            <p className="muted">Local technicians and after-sales support for installed equipment.</p>
          </article>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="hi-section container hi-testimonials">
        <h3>What clients say</h3>
        <div className="testimonials-grid">
          <blockquote className="testimonial">
            <p>“Fast, reliable and professional — their equipment and support improved our operating room workflow.”</p>
            <footer className="muted">— St. Mary Hospital</footer>
          </blockquote>

          <blockquote className="testimonial">
            <p>“Excellent logistics and documentation; they helped us import critical diagnostic tools quickly.”</p>
            <footer className="muted">— Regional Health Authority</footer>
          </blockquote>
        </div>
      </section>

      {/* PRODUCTS / ACTION */}
      <Element name="products-services-section" />
      <section className="hi-section container hi-products">
        <div className="hi-products-row">
          <div>
            <h3>Products & services</h3>
            <p className="muted">Diagnostic equipment, surgical instruments, hospital furniture and end-to-end procurement support — contact us for a bespoke solution.</p>
          </div>

          <div className="product-actions">
            <button className="btn primary" onClick={goToProducts}>Browse catalogue</button>
            <button className="btn ghost" onClick={() => scrollTo('contact-section')}>Get a quote</button>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <Element name="contact-section" />
      <section className="hi-section container hi-contact">
        <div className="contact-panel">
          <Contact />
        </div>
      </section>
    </main>
  );
};

export default Home;
